/*
 * Pixiwoo homepage v4 — vanilla behaviour for the pxw-v4-* sections.
 *  1. Arc carousel of design collections (same offset maths as positionFor() in the React export)
 *  2. Switching design swaps heading, accent glow and the "where this design lives" grid
 *  3. Discover tabs (By subject / Gift for…) + horizontal tracks with prev/next
 * Re-initialises on theme-editor section reloads. Honours prefers-reduced-motion.
 */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var POSITIONS = ['design-card--m2', 'design-card--m1', 'design-card--active', 'design-card--p1', 'design-card--p2', 'design-card--hidden'];

  function positionFor(index, active, count) {
    var raw = (index - active + count) % count;
    var offset = raw > count / 2 ? raw - count : raw;
    if (offset === -2) return 'design-card--m2';
    if (offset === -1) return 'design-card--m1';
    if (offset === 1) return 'design-card--p1';
    if (offset === 2) return 'design-card--p2';
    if (Math.abs(offset) > 2) return 'design-card--hidden';
    return 'design-card--active';
  }

  function pad(n) {
    return (n < 10 ? '0' : '') + n;
  }

  /* ---------- 1 + 2: design collections ---------- */
  function initCollections(root) {
    if (root.dataset.pxwReady) return;
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-design-card]'));
    var count = cards.length;
    if (!count) return;
    root.dataset.pxwReady = 'true';

    var title = root.querySelector('[data-design-title]');
    var story = root.querySelector('[data-design-story]');
    var counter = root.querySelector('[data-design-counter]');
    var panels = root.querySelectorAll('[data-design-panel]');
    var active = 0;

    function render(next) {
      active = (next + count) % count;
      var card = cards[active];

      cards.forEach(function (el, i) {
        POSITIONS.forEach(function (c) { el.classList.remove(c); });
        var pos = positionFor(i, active, count);
        el.classList.add(pos);
        el.setAttribute('aria-pressed', i === active ? 'true' : 'false');
        // Cards that are fully hidden should not be reachable by keyboard.
        el.tabIndex = pos === 'design-card--hidden' ? -1 : 0;
      });

      if (title) title.textContent = card.dataset.title || '';
      if (story) {
        story.textContent = card.dataset.story || '';
        story.hidden = !card.dataset.story;
      }
      if (counter) counter.textContent = pad(active + 1) + ' / ' + pad(count);
      if (card.dataset.accent) root.style.setProperty('--collection-accent', card.dataset.accent);

      Array.prototype.forEach.call(panels, function (panel) {
        var show = Number(panel.dataset.designPanel) === active;
        if (show && panel.hidden) {
          panel.hidden = false;
          // Restart the basesIn entrance animation, like the React key change did.
          var grid = panel.querySelector('.base-grid');
          if (grid && !reducedMotion.matches) {
            grid.style.animation = 'none';
            void grid.offsetWidth;
            grid.style.animation = '';
          }
        } else if (!show) {
          panel.hidden = true;
        }
      });
    }

    cards.forEach(function (card, i) {
      card.addEventListener('click', function () {
        if (i !== active) render(i);
      });
    });

    var prev = root.querySelector('[data-design-prev]');
    var next = root.querySelector('[data-design-next]');
    if (prev) prev.addEventListener('click', function () { render(active - 1); });
    if (next) next.addEventListener('click', function () { render(active + 1); });

    var stage = root.querySelector('.design-stage');
    if (stage) {
      stage.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowLeft') { event.preventDefault(); render(active - 1); cards[active].focus(); }
        if (event.key === 'ArrowRight') { event.preventDefault(); render(active + 1); cards[active].focus(); }
      });

      // Swipe on touch screens (horizontal only; vertical page scroll is left alone via touch-action: pan-y).
      var startX = null;
      var startY = null;
      stage.addEventListener('pointerdown', function (event) {
        if (event.pointerType === 'mouse') return;
        startX = event.clientX;
        startY = event.clientY;
      });
      stage.addEventListener('pointerup', function (event) {
        if (startX === null) return;
        var dx = event.clientX - startX;
        var dy = event.clientY - startY;
        startX = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) render(active + (dx < 0 ? 1 : -1));
      });
      stage.addEventListener('pointercancel', function () { startX = null; });
    }

    render(0);
  }

  /* ---------- 3: tabs + horizontal tracks ---------- */
  function initDiscover(root) {
    if (root.dataset.pxwReady) return;
    root.dataset.pxwReady = 'true';

    var tabs = Array.prototype.slice.call(root.querySelectorAll('[role="tab"]'));

    function select(tab, focus) {
      tabs.forEach(function (t) {
        var on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        var panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
      if (focus) tab.focus();
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { select(tab, false); });
      tab.addEventListener('keydown', function (event) {
        var target = null;
        if (event.key === 'ArrowRight') target = tabs[(i + 1) % tabs.length];
        if (event.key === 'ArrowLeft') target = tabs[(i - 1 + tabs.length) % tabs.length];
        if (event.key === 'Home') target = tabs[0];
        if (event.key === 'End') target = tabs[tabs.length - 1];
        if (target) { event.preventDefault(); select(target, true); }
      });
    });

    Array.prototype.forEach.call(root.querySelectorAll('[data-track-scroll]'), function (button) {
      button.addEventListener('click', function () {
        var track = document.getElementById(button.getAttribute('aria-controls'));
        if (!track) return;
        var card = track.querySelector('.story-card');
        var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        var step = card ? card.getBoundingClientRect().width + gap : 460;
        track.scrollBy({
          left: step * Number(button.dataset.trackScroll),
          behavior: reducedMotion.matches ? 'auto' : 'smooth'
        });
      });
    });
  }

  function init(scope) {
    var base = scope || document;
    Array.prototype.forEach.call(base.querySelectorAll('[data-pxw-v4-collections]'), initCollections);
    Array.prototype.forEach.call(base.querySelectorAll('[data-pxw-v4-discover]'), initDiscover);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });

  // Theme editor: selecting a design block jumps the carousel to it.
  document.addEventListener('shopify:block:select', function (event) {
    var card = event.target.closest && event.target.closest('[data-design-card]');
    if (card) card.click();
  });
})();
